import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ViewChildren, QueryList } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FilterOrSortDropdownType, FilterSelectedEventType, TableTypes } from '../../models/types';
import { DatepickerChangeEvent } from '../icam-datepicker/icam-datepicker.component';
import { ThemeService } from 'src/app/services/theme.service';
import { IcamSelectComponent } from '../icam-select/icam-select.component';
import { IcamDatepickerComponent } from '../icam-datepicker/icam-datepicker.component';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss'
})
export class FiltersComponent implements OnInit, OnChanges {
  @Input() fields: FilterOrSortDropdownType[] = [];
  @Input() sortingOptions: FilterOrSortDropdownType[] = [];
  @Input() canOrder!: boolean;
  @Input() translateOptions = true;
  @Input() tableType: TableTypes | null = null;
  @Input() filters: any = null;
  @Input() hasKeywordFilter = false;
  @Input() useInlineKeywordFilter = false;
  @Input() keywordFilterName = 'keyword';
  @Input() keywordFilterPlaceholder = 'FILTER.SEARCH_PLACEHOLDER';
  @Input() initialFilters: any = {};

  @Output() selectedFilters = new EventEmitter<FilterSelectedEventType[]>();
  @Output() resetFilters = new EventEmitter();

  @ViewChildren(IcamSelectComponent) selectComponents!: QueryList<IcamSelectComponent>;
  @ViewChildren(IcamDatepickerComponent) datepickerComponents!: QueryList<IcamDatepickerComponent>;

  private _selectedFilters: FilterSelectedEventType[] = [];
  selectedValue = false;
  isLight = false;

  keywordSearchControl = new FormControl('');

  constructor(private _themeService: ThemeService) {}

  ngOnInit() {
    this._themeService.refresh$
      .pipe(switchMap(() => this._themeService.isDarkTheme$.pipe(tap((x) => (this.isLight = !x)))))
      .subscribe();

    this.keywordSearchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
      this.onKeywordSearch(value || '');
    });

    this.checkForActiveFilters();

    this.initializeKeywordFilter();
    this.initializeActiveFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fields']) {
      this.checkForActiveFilters();
      if (this.fields && this.fields.length > 0) {
        this.initializeActiveFilters();
      }
    }

    if (changes['initialFilters'] && !changes['initialFilters'].firstChange) {
      this.initializeKeywordFilter();
      this.initializeActiveFilters();
    }
  }

  private initializeKeywordFilter() {
    if (this.initialFilters && this.initialFilters[this.keywordFilterName]) {
      this.keywordSearchControl.setValue(this.initialFilters[this.keywordFilterName], { emitEvent: false });
      this.selectedValue = true;
    }
  }

  private checkForActiveFilters() {
    const hasSelectedFilters = this.fields?.some((field) => field.values?.some((value) => value.selected === true));

    const hasKeywordFilter = this.initialFilters && this.initialFilters[this.keywordFilterName];

    if (hasSelectedFilters || hasKeywordFilter) {
      this.selectedValue = true;
    }
  }

  private initializeActiveFilters() {
    if (!this.initialFilters) return;

    this._selectedFilters = this._selectedFilters.filter((filter) => filter.name === this.keywordFilterName);

    Object.keys(this.initialFilters).forEach((filterName) => {
      if (this.initialFilters[filterName]) {
        const selectedValue = this.initialFilters[filterName];

        // Skip custom_period as it's handled separately via startDate/endDate
        if (filterName === 'custom_period') {
          return;
        }

        const field = this.fields?.find((f) => f.key === filterName);
        const option = field?.values?.find((v) => v.id === selectedValue || v.value === selectedValue);

        const filterEvent: FilterSelectedEventType = {
          name: filterName,
          selectedValue: selectedValue,
          operation: 'filter',
          text: option?.value || selectedValue
        };

        this._selectedFilters.push(filterEvent);
      }
    });

    if (this._selectedFilters.length > 0) {
      this.selectedValue = true;
    }
  }

  filterSelected(selectedFilter: FilterSelectedEventType) {
    this.selectedValue = true;
    this._selectedFilters = this._selectedFilters.filter((filter) => filter.name !== selectedFilter.name);

    // Se cambia machineType, resetta idMachine (le opzioni cambieranno)
    if (selectedFilter.name === 'machineType') {
      this._selectedFilters = this._selectedFilters.filter((filter) => filter.name !== 'idMachine');
    }

    if (selectedFilter.name === 'accountId' && selectedFilter.metadata && selectedFilter.metadata.accountId) {
      const modifiedFilter: FilterSelectedEventType = {
        name: selectedFilter.name,
        selectedValue: selectedFilter.metadata.accountId,
        operation: selectedFilter.operation,
        text: selectedFilter.text
      };
      this._selectedFilters.push(modifiedFilter);
      this.selectedFilters.emit(this._selectedFilters);
    } else {
      this._selectedFilters.push(selectedFilter);
      this.selectedFilters.emit(this._selectedFilters);
    }
  }

  onDatepickerChange(event: DatepickerChangeEvent) {
    this.selectedValue = true;
    this._selectedFilters = this._selectedFilters.filter((filter) => filter.name !== event.name);

    const filterEvent: FilterSelectedEventType = {
      name: event.name,
      selectedValue: event.selectedValue,
      operation: 'filter',
      text: event.text
    };

    this._selectedFilters.push(filterEvent);
    this.selectedFilters.emit(this._selectedFilters);
  }

  onKeywordSearch(value: string) {
    this.selectedValue = true;
    this._selectedFilters = this._selectedFilters.filter((filter) => filter.name !== this.keywordFilterName);
    this._selectedFilters.push({
      name: this.keywordFilterName,
      selectedValue: value,
      operation: 'filter',
      text: value
    });
    this.selectedFilters.emit(this._selectedFilters);
  }

  onResetFilters() {
    this._selectedFilters = [];
    this.selectedFilters.emit(this._selectedFilters);
    this.resetFilters.emit();
    this.selectedValue = false;
    this.keywordSearchControl.setValue('', { emitEvent: false });

    this.selectComponents?.forEach((select) => select.clearSelection());
    this.datepickerComponents?.forEach((datepicker) => datepicker.clearDateSelection());
  }
}
